import { useState, useEffect, useRef } from 'react';
import { rezensionen } from '../../interfaces/rezensionen';
import './RezensionenSlider.css';

export default function RezensionenSlider() {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [visibleCards, setVisibleCards] = useState(3); // Anzahl der sichtbaren Karten
	const autoplayRef = useRef<NodeJS.Timeout | null>(null);

	// Alle Boxen gleich hoch setzen
	useEffect(() => {
		const setEqualHeights = () => {
			const cards = Array.from(document.querySelectorAll<HTMLElement>('.rezension-card'));
			if (!cards.length) return;

			// Höhe zurücksetzen, um exakte natürliche Höhe zu messen
			cards.forEach(card => { card.style.height = 'auto'; });

			const maxHeight = cards.reduce((max, card) => Math.max(max, card.offsetHeight), 0);
			cards.forEach(card => { card.style.height = `${maxHeight}px`; });
		};

		// Initial und bei Resize anwenden
		setEqualHeights();
		window.addEventListener('resize', setEqualHeights);

		// Nachdem Bilder/Fonts geladen sind, erneut setzen
		window.requestAnimationFrame(() => setEqualHeights());

		return () => {
			window.removeEventListener('resize', setEqualHeights);
		};
	}, []);

	// Anzahl der sichtbaren Karten basierend auf Bildschirmgröße
	useEffect(() => {
		const updateVisibleCards = () => {
			if (window.innerWidth <= 768) {
				setVisibleCards(1);
			} else {
				// Ab > 768px: 2 Karten anzeigen (breitere Boxen als vorher)
				setVisibleCards(2);
			}
		};

		updateVisibleCards();
		window.addEventListener('resize', updateVisibleCards);
		return () => window.removeEventListener('resize', updateVisibleCards);
	}, []);

	// Autoplay-Funktionalität
	useEffect(() => {
		const startAutoplay = () => {
			autoplayRef.current = setInterval(() => {
				setCurrentIndex((prevIndex) => {
					const maxIndex = Math.max(0, rezensionen.length - visibleCards);
					if (prevIndex >= maxIndex) {
						return 0; // Zurück zum Anfang, wenn am Ende
					}
					return prevIndex + 1;
				});
			}, 6000); // Wechsel alle 6 Sekunden (langsamer)
		};

		startAutoplay();

		return () => {
			if (autoplayRef.current) {
				clearInterval(autoplayRef.current);
			}
		};
	}, [visibleCards]);

	const handlePrevious = () => {
		setCurrentIndex((prevIndex) => {
			const maxIndex = Math.max(0, rezensionen.length - visibleCards);
			return prevIndex === 0 ? maxIndex : prevIndex - 1;
		});
		// Autoplay neu starten
		if (autoplayRef.current) {
			clearInterval(autoplayRef.current);
		}
		const newInterval = setInterval(() => {
			setCurrentIndex((prevIndex) => {
				const maxIndex = Math.max(0, rezensionen.length - visibleCards);
				if (prevIndex >= maxIndex) {
					return 0;
				}
				return prevIndex + 1;
			});
		}, 6000);
		autoplayRef.current = newInterval;
	};

	const handleNext = () => {
		setCurrentIndex((prevIndex) => {
			const maxIndex = Math.max(0, rezensionen.length - visibleCards);
			if (prevIndex >= maxIndex) {
				return 0; // Zurück zum Anfang, wenn am Ende
			}
			return prevIndex + 1;
		});
		// Autoplay neu starten
		if (autoplayRef.current) {
			clearInterval(autoplayRef.current);
		}
		const newInterval = setInterval(() => {
			setCurrentIndex((prevIndex) => {
				const maxIndex = Math.max(0, rezensionen.length - visibleCards);
				if (prevIndex >= maxIndex) {
					return 0;
				}
				return prevIndex + 1;
			});
		}, 6000);
		autoplayRef.current = newInterval;
	};

	const handleDotClick = (index: number) => {
		setCurrentIndex(index);
		// Autoplay neu starten
		if (autoplayRef.current) {
			clearInterval(autoplayRef.current);
		}
		const newInterval = setInterval(() => {
			setCurrentIndex((prevIndex) => {
				const maxIndex = Math.max(0, rezensionen.length - visibleCards);
				if (prevIndex >= maxIndex) {
					return 0;
				}
				return prevIndex + 1;
			});
		}, 6000);
		autoplayRef.current = newInterval;
	};

	const renderStars = (rating: number) => {
		return Array.from({ length: 5 }, (_, index) => (
			<span
				key={index}
				className={`star ${index < rating ? 'filled' : ''}`}
			>
				★
			</span>
		));
	};

	// Berechne maxIndex korrekt: bei 6 Rezensionen und 2 sichtbaren = maxIndex 4 (zeigt Rezensionen 4-5)
	const maxIndex = rezensionen.length > visibleCards ? rezensionen.length - visibleCards : 0;
	const dotsCount = maxIndex + 1;

	return (
		<div className="rezensionen-slider-container">
			<h3>Kundenbewertungen</h3>
			<div className="rezensionen-slider-wrapper">
				<button
					className="slider-button slider-button-prev"
					onClick={handlePrevious}
					aria-label="Vorherige Bewertungen"
				>
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>

				<div className="rezensionen-slider">
					<div
						className={`rezensionen-slider-track visible-cards-${visibleCards}`}
						style={{
							transform: `translateX(-${currentIndex * (100 / visibleCards)}%)`,
						}}
					>
						{rezensionen.map((rezension, index) => (
							<div key={`rezension-${index}`} className="rezension-card">
								<div className="rezension-header">
									<h3>{rezension.name}</h3>
									<div className="rating-stars">
										{renderStars(rezension.rating)}
									</div>
								</div>
								<p className="rezension-text">{rezension.bewertung}</p>
							</div>
						))}
					</div>
				</div>

				<button
					className="slider-button slider-button-next"
					onClick={handleNext}
					aria-label="Nächste Bewertungen"
				>
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>
			</div>

			<div className="slider-dots">
				{Array.from({ length: dotsCount }, (_, index) => (
					<button
						key={index}
						className={`dot ${index === currentIndex ? 'active' : ''}`}
						onClick={() => handleDotClick(index)}
						aria-label={`Gehe zu Bewertung ${index + 1}`}
					/>
				))}
			</div>
		</div>
	);
}
